import { t, useLanguage } from "@/lib/i18n";
import ReligionSelect from "@/components/ReligionSelect";
import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel } from "@/components/ui/alert-dialog";

export default function CasteAdmin({ embedded = false }) {
  useLanguage();
  const { user, updateUser } = useAuth();
  const [castes, setCastes] = useState([]);
  const [religion, setReligion] = useState("Hindu");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [removing, setRemoving] = useState(null);
  const [assigned, setAssigned] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [renaming, setRenaming] = useState(null);
  const [newName, setNewName] = useState("");
  const [savingRename, setSavingRename] = useState(false);
  useEffect(() => {
    api.get("/castes").then(({ data }) => setCastes(data))
      .catch(() => setError("Could not load Caste list. Please reload."))
      .finally(() => setLoading(false));
  }, []);
  const visibleCastes = castes.filter((c) => c.religion === religion);
  if (user?.role !== "admin") return <Navigate to="/app" replace />;
  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      const { data } = await api.post("/castes", { name, religion });
      setCastes((old) => [...old, data].sort((a, b) => a.name.localeCompare(b.name)));
      setName("");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Could not add Caste");
    } finally { setSaving(false); }
  };
  const remove = async () => {
    setDeleting(true);
    setError("");
    try {
      await api.delete("/castes", { params: { name: removing.name, religion: removing.religion, confirm_assigned: !!assigned } });
      setCastes((current) => current.filter((c) => !(c.name === removing.name && c.religion === removing.religion)));
      if (user.caste === removing.name && user.religion === removing.religion) updateUser({ ...user, caste: "" });
      setRemoving(null);
      setAssigned(null);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 409 && detail?.code === "caste_assigned") setAssigned(detail);
      else setError(formatApiErrorDetail(detail) || "Could not remove Caste. Please try again.");
    } finally { setDeleting(false); }
  };
  const rename = async (event) => {
    event.preventDefault();
    setSavingRename(true);
    setError("");
    try {
      const { data } = await api.put("/castes", { name: newName, religion: renaming.religion }, { params: { name: renaming.name } });
      setCastes((current) => current.map((c) => (c.name === renaming.name && c.religion === renaming.religion) ? data : c)
        .sort((a, b) => a.name.localeCompare(b.name)));
      if (user.caste === renaming.name && user.religion === renaming.religion) updateUser({ ...user, caste: data.name });
      setRenaming(null);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Could not rename Caste. Please try again.");
    } finally { setSavingRename(false); }
  };
  const Container = embedded ? "section" : "main";
  const Heading = embedded ? "h2" : "h1";
  return <Container className={embedded ? "" : "min-h-screen bg-[#FAF7F2] p-4 sm:p-6"}>
    <div className="max-w-xl mx-auto space-y-6">
      {!embedded && <Link to="/app" className="text-amber-800 underline">{t("Back to tree")}</Link>}
      <Heading className="font-serif text-3xl">{t("Caste administration")}</Heading>
      <p className="text-stone-600">{t("Manage Castes for the selected Religion. Caste names must be unique within that Religion. Remove a Caste's Gotras before removing it.")}</p>
      {error && <p role="alert" className="text-red-700">{t(error)}</p>}
      <ReligionSelect id="admin-caste-religion" value={religion} onChange={(value) => { setReligion(value); }} />
      <form onSubmit={submit} className="space-y-3 bg-white border rounded-xl p-5">
        <Label htmlFor="caste-name">{t("New Caste")}</Label>
        <Input id="caste-name" value={name} onChange={(e) => setName(e.target.value)} maxLength={100} required />
        <Button disabled={saving || loading || !religion || !name.trim()} type="submit" className="gold-btn">{saving ? t("Adding…") : t("Add Caste")}</Button>
      </form>
      {loading ? <p>{t("Loading…")}</p> : <ul className="bg-white border rounded-xl divide-y">
        {visibleCastes.map((c) => <li key={JSON.stringify([c.religion, c.name])} className="p-3 flex flex-wrap items-center justify-between gap-3">
          <span className="min-w-0 break-words">{c.name}</span>
          <div className="shrink-0 flex gap-2">
          <Button variant="outline" size="sm" aria-label={t("Rename {0}", { 0: c.name })} disabled={deleting || savingRename}
            onClick={() => { setError(""); setNewName(c.name); setRenaming(c); }}>{t("Rename")}</Button>
          <Button variant="outline" size="sm" className="shrink-0 text-red-700" aria-label={t("Remove {0}", { 0: c.name })}
            disabled={deleting} onClick={() => { setError(""); setAssigned(null); setRemoving(c); }}>{t("Remove")}</Button>
          </div>
        </li>)}
        {!visibleCastes.length && <li className="p-3 text-stone-500">{t("No names added yet.")}</li>}
      </ul>}
      <Dialog open={!!renaming} onOpenChange={(open) => { if (!open && !savingRename) setRenaming(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("Rename Caste")}</DialogTitle>
            <DialogDescription>{t("Member assignments, account defaults, and member requests will use the new name.")}</DialogDescription>
          </DialogHeader>
          <form onSubmit={rename} className="space-y-4">
            <Label htmlFor="rename-caste-name">{t("New name")}</Label>
            <Input id="rename-caste-name" value={newName} onChange={(e) => setNewName(e.target.value)} maxLength={100} required disabled={savingRename} />
            {error && <p role="alert" className="text-red-700">{t(error)}</p>}
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" disabled={savingRename} onClick={() => setRenaming(null)}>{t("Cancel")}</Button>
              <Button type="submit" disabled={savingRename || !newName.trim() || newName.trim() === renaming?.name} data-testid="confirm-rename-caste">{savingRename ? t("Saving...") : t("Save name")}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
      <AlertDialog open={!!removing} onOpenChange={(open) => { if (!open && !deleting) { setRemoving(null); setAssigned(null); } }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{assigned ? t("Confirm removal of assigned caste") : t("Remove Caste?")}</AlertDialogTitle>
            <AlertDialogDescription>
              {assigned
                ? t("{0} is assigned to {1} member(s) and {2} account default(s). Removing it will clear these assignments. Member records will be kept. Are you sure you want to continue?", { 0: removing?.name, 1: assigned.members, 2: assigned.accounts })
                : t("Remove {0} from the Caste list?", { 0: removing?.name })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {error && <p role="alert" className="text-red-700">{t(error)}</p>}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>{t("Cancel")}</AlertDialogCancel>
            <Button variant="destructive" disabled={deleting} onClick={remove} data-testid="confirm-remove-caste">
              {deleting ? t("Removing...") : assigned ? t("Remove and clear assignments") : t("Remove")}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  </Container>;
}
